/**
 * Maskito options for manually typed MM/DD/YYYY dates, so a user never has to
 * type the '/' characters themselves.
 *
 * Entering "01021990" fills the slashes in, entering "01/02/1990" is accepted
 * as typed, and the mask rejects impossible segments (month 13, Feb 30, and so
 * on) as they are entered. Applied by components/datepicker.vue.
 *
 * @see https://maskito.dev/kit/date
 */
import { maskitoDate } from '@maskito/kit';
import * as chrono from 'chrono-node';

const SEPARATOR = '/';
const MODE = 'mm/dd/yyyy';

// Digit counts for MM / DD / YYYY.
const SEGMENT_SIZES = [2, 2, 4];

// Matches a value made up only of digits and separators.
const NUMERIC_DATE = /^[0-9/]*$/;

// A slash-delimited numeric date with at least one separator, e.g. "1/2/1990".
// Digit-only input like "01021990" is left for the mask to slot itself.
const SLASH_NUMERIC_DATE = /^\d{1,4}(?:\/\d{0,4}){1,2}$/;

function pad(number, size) {
  return String(number).padStart(size, '0');
}

/**
 * Parses a free-form date string and returns it as MM/DD/YYYY, or null when
 * chrono can't read it. Shared by the mask preprocessor and the datepicker
 * component so both normalize autofilled/pasted dates the same way.
 */
export function toMaskedDate(text) {
  if (text == null) return null;

  const parsed = chrono.parseDate(String(text));
  if (parsed === null) return null;

  return [
    pad(parsed.getMonth() + 1, 2),
    pad(parsed.getDate(), 2),
    pad(parsed.getFullYear(), 4),
  ].join(SEPARATOR);
}

/**
 * Normalizes a long-form date (like one from browser autofill) into MM/DD/YYYY
 * before the mask runs, so the mask receives a value it can format cleanly.
 * Numeric input is left alone, and values chrono can't parse pass through.
 */
function normalizeLongFormDate({ elementState, data }) {
  const { value, selection } = elementState;
  const [from, to] = selection;
  const candidate = value.slice(0, from) + data + value.slice(to);

  if (candidate === '' || NUMERIC_DATE.test(candidate)) {
    return { elementState, data };
  }

  const formatted = toMaskedDate(candidate);
  if (formatted === null) return { elementState, data };

  // Select the whole value so the mask replaces it instead of appending.
  return {
    elementState: { value, selection: [0, value.length] },
    data: formatted,
  };
}

/**
 * Normalizes a slash-delimited numeric date that arrives all at once (browser
 * autofill or paste) into MM/DD/YYYY before the mask runs.
 *
 * padSegmentOnSeparator() only fires on a single typed "/", so a pasted
 * "1/2/1990" never gets its segments padded and the mask drops the separators,
 * silently producing "12/19/90". This pads the month and day segments so the
 * mask sees a well-formed value. The year is left as typed, and digit-only
 * input (no separator) is left for the mask to slot itself.
 */
function normalizeNumericDate({ elementState, data }) {
  const { value, selection } = elementState;
  const [from, to] = selection;
  const candidate = value.slice(0, from) + data + value.slice(to);

  // Only whole-value insertions (paste/autofill), not per-character typing.
  if (data.length <= 1 || !SLASH_NUMERIC_DATE.test(candidate)) {
    return { elementState, data };
  }

  const segments = candidate.split(SEPARATOR);
  const normalized = segments
    .map((segment, index) => {
      const size = SEGMENT_SIZES[index];
      const isYear = index >= SEGMENT_SIZES.length - 1;

      // Leave the year alone; pad only partial, non-empty month/day segments.
      if (isYear || size === undefined || !segment || segment.length >= size) {
        return segment;
      }

      return segment.padStart(size, '0');
    })
    .join(SEPARATOR);

  if (normalized === candidate) return { elementState, data };

  // Select the whole value so the mask replaces it instead of appending.
  return {
    elementState: { value, selection: [0, value.length] },
    data: normalized,
  };
}

/**
 * Maskito drops a separator typed before its segment is full, because the
 * character does not fit the mask template yet. Left alone, "1/2/1990" ends up
 * as "12/19/90" -- a silently wrong date. Pad the open segment ("1" -> "01") so
 * the separator lands where the template expects it.
 */
function padSegmentOnSeparator({ elementState, data }) {
  if (data !== SEPARATOR) return { elementState, data };

  const { value, selection } = elementState;
  const [from, to] = selection;

  // Only when the caret sits at the end of what has been typed so far.
  if (from !== to || from !== value.length) return { elementState, data };

  const segments = value.split(SEPARATOR);
  const index = segments.length - 1;
  const segment = segments[index];
  const size = SEGMENT_SIZES[index];

  // Nothing typed yet, segment already full, or no separator follows it.
  if (!segment || !size || segment.length >= size || index >= SEGMENT_SIZES.length - 1) {
    return { elementState, data };
  }

  const padded = [
    ...segments.slice(0, index),
    segment.padStart(size, '0'),
  ].join(SEPARATOR);

  return {
    elementState: { value: padded, selection: [padded.length, padded.length] },
    data,
  };
}

const dateMaskOptions = maskitoDate({ mode: MODE, separator: SEPARATOR });

export { normalizeLongFormDate, normalizeNumericDate, padSegmentOnSeparator };

export default {
  ...dateMaskOptions,
  preprocessors: [
    normalizeLongFormDate,
    normalizeNumericDate,
    padSegmentOnSeparator,
    ...dateMaskOptions.preprocessors,
  ],
};
